import React, { useState, useContext, useEffect } from 'react';
import StoreContext from '../../context/StoreContext';
import {
  HtmlContent,
} from '../common';
import {
  VariantSelector,
  QuantitySelector,
} from '.';
import { getPrice } from '../../util/helpers';
import './ProductDetails.scss';

const AddToCartButton = (props) => {
  const {
    isAdding,
    isAvailable,
    productName,
    addToCartHandler,
  } = props;
  let buttonLabel = 'Add to Cart';
  let buttonAria = `Add ${productName} to cart`;
  let buttonClasses = 'add-to-cart-button styled-button styled-button-primary';
  if (isAdding) {
    buttonLabel = 'Adding...';
    buttonAria = `Button disabled, adding ${productName} to cart`;
    buttonClasses = 'add-to-cart-button styled-button styled-button-primary';
  }
  if (!isAvailable) {
    buttonLabel = 'Out of Stock';
    buttonAria = `Button disabled. Sorry, ${productName} is out of stock`;
    buttonClasses = 'add-to-cart-button styled-button styled-button-primary';
  }

  return (
    <button
      className={buttonClasses}
      type="button"
      disabled={!isAvailable || isAdding}
      onClick={addToCartHandler}
      aria-label={buttonAria}
    >
      <span>{buttonLabel}</span>
    </button>
  );
};

const ProductDetails = ({ product }) => {
  const [quantity, setQuantity] = useState(1);
  const [variant, setVariant] = useState(product.variants[0]);
  const context = useContext(StoreContext);
  const hasVariants = product.variants.length > 1;
  const {
    client: {
      product: {
        helpers: {
          variantForOptions,
        },
      },
    },
    adding,
  } = context;
  const {
    title,
    descriptionHtml,
  } = product;
  const productVariant = variantForOptions(product, variant) || variant;
  const displayPrice = getPrice(productVariant.price);
  const [available, setAvailable] = useState(productVariant.availableForSale);

  useEffect(() => {
    const defaultOptionValues = {};
    product.options.forEach((selector) => {
      const { name, values } = selector;
      defaultOptionValues[name] = values[0]; // eslint-disable-line
    });
    setVariant(defaultOptionValues);
  }, []);

  useEffect(() => {
    checkAvailability(product.shopifyId);
  }, [productVariant]);

  const checkAvailability = (productId) => {
    context.client.product.fetch(productId).then((realtimeProduct) => {
      // this checks the currently selected variant for availability
      const result = realtimeProduct.variants.filter(
        reatimeVariant => reatimeVariant.id === productVariant.shopifyId,
      );
      setAvailable(result[0].available);
    });
  };
  const increaseQuantity = () => setQuantity(quantity + 1);
  const decreaseQuantity = () => setQuantity(quantity - 1);

  const handleOptionChange = (selectedOption, optionName) => {
    setVariant(prevState => ({
      ...prevState,
      [optionName]: selectedOption.value,
    }));
  };

  const handleAddToCart = () => context.addVariantToCart(productVariant.shopifyId, quantity);

  const variantSelectors = hasVariants
    ? product.options.map(option => (
      <VariantSelector
        currentValue={variant[option.name]}
        onChange={selectedOption => handleOptionChange(selectedOption, option.name)}
        key={option.id.toString()}
        option={option}
      />
    ))
    : null;

  return (
    <div className="product-details">
      <h1 className="product-title">{title}</h1>
      <span
        className="price"
      >
        {displayPrice}
      </span>
      <div className="product-description">
        <HtmlContent content={descriptionHtml} />
      </div>
      <div className="variant-selectors">
        {variantSelectors}
      </div>
      <QuantitySelector
        productName={title}
        quantity={quantity}
        decreaseQuantity={decreaseQuantity}
        increaseQuantity={increaseQuantity}
        hasLabel
      />
      <AddToCartButton
        isAdding={adding}
        isAvailable={available}
        productName={title}
        addToCartHandler={handleAddToCart}
      />
    </div>
  );
};

export default ProductDetails;
